import { useEffect, useState } from 'react';
import { StripeService } from 'lib/apis/StripeServiceNew';

// How long a fetched promotion is reused, so checkout shows the plan page's result at once
// instead of fetching again. Kept in memory only, never in storage, so a sale still switches
// on and off on time.
const CACHE_TTL_MS = 5 * 60 * 1000;
// How long a page waits for the promotion before showing normal prices instead.
export const PROMOTION_WAIT_MS = 1500;

// One request shared by every caller: { promise, value, settled, fetchedAt }. The value is
// { all, month, year }: the promotion covering every billing frequency, then the one for each.
let cachedRequest = null;

const NO_PROMOTIONS = { all: null, month: null, year: null };

// An API without per-frequency promotions only sends `promotion`, which covers both.
const toPromotions = (data) => {
    const all = data?.promotion ?? null;
    const byFrequency = data?.promotions;
    return {
        all,
        month: byFrequency ? byFrequency.month ?? null : all,
        year: byFrequency ? byFrequency.year ?? null : all,
    };
};

const isLive = (promotion) =>
    Number(promotion?.discountPercent) > 0 &&
    (!promotion.endsAt || new Date(promotion.endsAt).getTime() > Date.now());

const freshCachedRequest = () =>
    cachedRequest && Date.now() - cachedRequest.fetchedAt < CACHE_TTL_MS ? cachedRequest : null;

const requestPromotion = () => {
    const existing = freshCachedRequest();
    if (existing) {
        return existing;
    }

    const request = { value: null, settled: false, fetchedAt: Date.now() };
    request.promise = Promise.resolve()
        .then(() => StripeService().getActivePromotion())
        .then((response) => toPromotions(response?.data))
        .catch(() => {
            // Don't keep a failure: the next page to ask tries again.
            if (cachedRequest === request) {
                cachedRequest = null;
            }
            return NO_PROMOTIONS;
        })
        .then((promotions) => {
            request.value = promotions;
            request.settled = true;
            return promotions;
        });

    cachedRequest = request;
    return request;
};

// For tests.
export const resetActivePromotionCache = () => {
    cachedRequest = null;
};

// The live checkout promotion for a billing frequency ('year' or 'month'); without one, only a
// promotion covering both. `isLoaded` turns true once the promotion is known, or after
// PROMOTION_WAIT_MS, so pages can hold the prices back rather than show them and then jump.
// Any failure means no promotion, so pages fall back to normal prices. With `disabled` (Signature
// Collection vendors never get the automatic sale) it never asks and reports no promotion.
export function useActivePromotion(billingFrequency, { disabled = false } = {}) {
    const [state, setState] = useState(() => {
        if (disabled) {
            return { promotions: NO_PROMOTIONS, isLoaded: true };
        }
        const cached = freshCachedRequest();
        return cached?.settled
            ? { promotions: cached.value, isLoaded: true }
            : { promotions: NO_PROMOTIONS, isLoaded: false };
    });

    useEffect(() => {
        if (disabled) {
            return undefined;
        }
        const request = requestPromotion();
        if (request.settled) {
            setState({ promotions: request.value, isLoaded: true });
            return undefined;
        }

        let isMounted = true;
        const waitTimer = setTimeout(() => {
            if (isMounted) {
                setState((current) => ({ ...current, isLoaded: true }));
            }
        }, PROMOTION_WAIT_MS);

        request.promise.then((promotions) => {
            clearTimeout(waitTimer);
            if (isMounted) {
                setState({ promotions, isLoaded: true });
            }
        });

        return () => {
            isMounted = false;
            clearTimeout(waitTimer);
        };
    }, [disabled]);

    const candidate = disabled ? null : state.promotions[billingFrequency || 'all'] ?? null;
    const promotion = isLive(candidate) ? candidate : null;
    return {
        promotion,
        discountPercent: promotion ? Number(promotion.discountPercent) : 0,
        isLoaded: disabled || state.isLoaded,
    };
}
